/**
 * Historiku i rekrutimit — kërkimi i njerëzve që tashmë kanë folur me kompaninë.
 *
 * Kjo faqe NUK është një kopje e tretë e listës së kandidatëve. «Kandidatët»
 * është puna e sotme: kush pret shqyrtim, kush ka intervistë nesër. Historiku
 * është pyetja e rrallë dhe e vlefshme: kur hapet një pozitë e re, a kemi folur
 * tashmë me dikë që mund të jetë i duhuri — edhe nëse ajo bisedë ka ndodhur para
 * dy vitesh dhe ka përfunduar me refuzim.
 *
 * Asgjë nuk arkivohet dhe asgjë nuk dublikohet: kërkimi shkon mbi të njëjtat
 * aplikime, me faqosje te serveri.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, MenuItem, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import HistoryIcon from '@mui/icons-material/History';
import { useHistory, useLocation } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import CandidateHistoryDrawer from '../components/recruitment/CandidateHistoryDrawer';
import recruitmentHistoryService from '../services/recruitmentHistoryService';
import applicationsService from '../services/applicationsService';
import { authFetch } from '../services/apiServices';
import { RECRUITMENT_STATUSES, statusLabel } from '../config/recruitmentStatuses';
import { rejectionLabel } from '../config/recruitmentPipeline';
import { sourceLabel } from '../config/recruitmentSources';

const STATUS_COLOR = Object.fromEntries(RECRUITMENT_STATUSES.map((x) => [x.key, x.color]));

const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x) ? '—' : `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`;
};

const activityText = (r) => [r.interviewCount ? `${r.interviewCount} intervista` : null,
  r.offerCount ? `${r.offerCount} oferta` : null].filter(Boolean).join(' · ') || '—';

function StatusCell({ r }) {
  return (
    <>
      <Chip size="small" color={STATUS_COLOR[r.status] || 'default'} label={statusLabel(r.status)} sx={{ height: 20, fontSize: '.66rem' }} />
      {r.status === 'rejected' && r.rejectionCode && (
        <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: 0.25 }}>{rejectionLabel(r.rejectionCode)}</Typography>
      )}
    </>
  );
}

function RowActions({ r, onHistory, onApplication }) {
  return (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      {r.candidateId ? (
        <Button size="small" sx={{ textTransform: 'none', fontSize: '.75rem' }}
          onClick={() => onHistory(r.candidateId)}>Historiku</Button>
      ) : (
        <Tooltip title="Pa email dhe pa telefon — s'ka identitet për ta ndjekur nëpër aplikime">
          <span><Button size="small" disabled sx={{ textTransform: 'none', fontSize: '.75rem' }}>Historiku</Button></span>
        </Tooltip>
      )}
      <Button size="small" sx={{ textTransform: 'none', fontSize: '.75rem' }}
        onClick={() => onApplication(r.applicationId)}>Aplikimi</Button>
    </Stack>
  );
}

function ClosedChip() {
  return (
    <Tooltip title="Pozita është mbyllur — të dhënat e rekrutimit mbeten">
      <Chip size="small" label="e mbyllur" sx={{ ml: 0.5, height: 17, fontSize: '.6rem' }} />
    </Tooltip>
  );
}

export default function RecruitmentHistoryPage() {
  const theme = useTheme();
  // Tetë kolona nuk hyjnë te telefoni: aty çdo aplikim bëhet kartë.
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({ search: '', outcome: 'all', job_posting_id: 'all', source: 'all', from: '', to: '' });
  const [postings, setPostings] = useState([]);
  const [openCandidate, setOpenCandidate] = useState(null);
  const [reload, setReload] = useState(0);

  // Pozitat aktive i duhen kutisë së ftesës; lista është e lehtë që nga Faza 4.
  useEffect(() => {
    authFetch('/api/job-postings').then((r) => r.json())
      .then((d) => setPostings((d?.data || []).map((p) => ({ id: p.id, title: p.title, isActive: p.isActive }))))
      .catch(() => {});
    applicationsService().recruiters().catch(() => {});
  }, []);

  // `?candidate=12` — Punësimet dhe Përmbledhja hapin drejt këtij personi.
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('candidate');
    if (id && /^\d+$/.test(id)) setOpenCandidate(Number(id));
  }, [location.search]);

  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(filters.search.trim()), 300);
    return () => clearTimeout(t);
  }, [filters.search]);
  useEffect(() => { setPage(0); }, [debounced, filters.outcome, filters.job_posting_id, filters.source, filters.from, filters.to]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const d = await recruitmentHistoryService().history({
      page: page + 1, limit: rowsPerPage, search: debounced,
      outcome: filters.outcome, job_posting_id: filters.job_posting_id,
      source: filters.source, from: filters.from, to: filters.to,
    });
    if (d.error) setError(d.error);
    setRows(d.data || []);
    setPagination(d.pagination || pagination);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debounced, filters.outcome, filters.job_posting_id, filters.source, filters.from, filters.to, reload]);

  useEffect(() => { load(); }, [load]);

  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Box>
      <PageHeader title="Historiku i rekrutimit"
        subtitle="Kërko njerëzit që tashmë kanë aplikuar — përfshirë pozitat e mbyllura dhe aplikimet e refuzuara" />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          {/* Rrjetë, jo shtyllë: te telefoni gjashtë fusha njëra nën tjetrën zinin
              gjithë ekranin para se të shihej rezultati i parë. */}
          <Box sx={{
            display: 'grid', gap: 1.5,
            gridTemplateColumns: { xs: '1fr 1fr', md: 'minmax(220px, 2fr) repeat(4, minmax(140px, 1fr)) auto' },
            alignItems: 'center',
          }}>
            <TextField size="small" label="Kërko" placeholder="Emër, email, telefon ose pozitë"
              value={filters.search} onChange={setF('search')} sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }} />
            <TextField select size="small" label="Përfundimi" value={filters.outcome} onChange={setF('outcome')}>
              <MenuItem value="all">Të gjitha</MenuItem>
              {RECRUITMENT_STATUSES.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Pozita" value={filters.job_posting_id} onChange={setF('job_posting_id')}>
              <MenuItem value="all">Të gjitha</MenuItem>
              {postings.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>{p.title}{p.isActive ? '' : ' (e mbyllur)'}</MenuItem>
              ))}
            </TextField>
            <TextField size="small" type="date" label="Prej" InputLabelProps={{ shrink: true }} value={filters.from} onChange={setF('from')} />
            <TextField size="small" type="date" label="Deri" InputLabelProps={{ shrink: true }} value={filters.to} onChange={setF('to')} />
            <Button size="small" variant="text" sx={{ gridColumn: { xs: '1 / -1', md: 'auto' }, justifySelf: { xs: 'end', md: 'auto' } }}
              onClick={() => setFilters({ search: '', outcome: 'all', job_posting_id: 'all', source: 'all', from: '', to: '' })}>Reseto</Button>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <CardContent sx={{ p: { xs: compact ? 0 : 1.5, md: 2 }, '&:last-child': { pb: { xs: compact ? 0 : 1.5, md: 2 } } }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
          ) : rows.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
              <HistoryIcon sx={{ color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                Asnjë aplikim nuk i përgjigjet kërkimit.
              </Typography>
            </Stack>
          ) : compact ? (
            <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
              {rows.map((r) => (
                <Box key={r.applicationId} sx={{ px: 2, py: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>{r.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', wordBreak: 'break-word' }}>
                        {[r.email, r.phone].filter(Boolean).join(' · ') || 'pa kontakt'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}><StatusCell r={r} /></Box>
                  </Stack>
                  <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
                    {r.position || '—'}{r.postingActive === false && <ClosedChip />}
                    <Box component="span" sx={{ color: 'text.secondary' }}> · {fmtDate(r.appliedAt)}</Box>
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25, mr: -1 }}>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', flex: 1, minWidth: 0 }}>
                      {[activityText(r), sourceLabel(r.source), r.recruiterName].filter((x) => x && x !== '—').join(' · ')}
                    </Typography>
                    <RowActions r={r} onHistory={setOpenCandidate} onApplication={(id) => history.push(`/applications/edit/${id}`)} />
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Personi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pozita</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Aplikoi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Përfundimi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Aktiviteti</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Burimi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Rekrutuesi</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.applicationId} hover>
                      <TableCell>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{r.name}</Typography>
                        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                          {[r.email, r.phone].filter(Boolean).join(' · ') || 'pa kontakt'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {r.position || '—'}
                        {r.postingActive === false && <ClosedChip />}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDate(r.appliedAt)}</TableCell>
                      <TableCell><StatusCell r={r} /></TableCell>
                      <TableCell sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {activityText(r)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{sourceLabel(r.source)}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{r.recruiterName || '—'}</TableCell>
                      <TableCell align="right">
                        <RowActions r={r} onHistory={setOpenCandidate} onApplication={(id) => history.push(`/applications/edit/${id}`)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.total > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={Math.min(page, Math.max(0, pagination.totalPages - 1))}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              labelRowsPerPage={compact ? '' : 'Rreshta:'}
              rowsPerPageOptions={compact ? [] : [25, 50, 100]}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
            />
          )}
        </CardContent>
      </Card>

      <CandidateHistoryDrawer
        candidateId={openCandidate}
        open={Boolean(openCandidate)}
        onClose={() => setOpenCandidate(null)}
        postings={postings.filter((p) => p.isActive)}
        onChanged={() => setReload((k) => k + 1)}
      />
    </Box>
  );
}
